import React, { useCallback, useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import ChannelIntegrationService from "../services/ChannelIntegrationService";
import {
  SA,
  SaCard,
  SaField,
  SaPrimaryButton,
  SaFormActions,
  SaSectionTitle,
  saInputSx,
} from "./shared/SuperAdminUi";

/* ────────────────────────────────────────────────────────────────
   Channel definitions — one entry per supported channel
   ──────────────────────────────────────────────────────────────── */
const CHANNELS = [
  {
    key: "GoogleAds",
    label: "Google Ads",
    brand: "Google",
    icon: "fab fa-google",
    color: "#4285F4",
    description:
      "Connect Google Ads to pull leads generated via Search, Display, or Performance Max campaigns. " +
      "Uses the Google Ads API (OAuth2 + Developer Token). Leads flow in automatically when a user submits a lead form.",
    fields: [
      { name: "customerId", label: "Customer ID", placeholder: "123-456-7890", hint: "10-digit Google Ads account ID (dashes optional)" },
      { name: "developerToken", label: "Developer Token", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxx", secret: true, hint: "From Google Ads API Center → Tools & Settings" },
      { name: "clientId", label: "OAuth Client ID", placeholder: "xxxx.apps.googleusercontent.com", hint: "From Google Cloud Console OAuth 2.0 credentials" },
      { name: "clientSecret", label: "OAuth Client Secret", placeholder: "GOCSPX-…", secret: true },
      { name: "refreshToken", label: "Refresh Token", placeholder: "1//0g…", secret: true, hint: "Long-lived token obtained after OAuth consent flow" },
      { name: "loginCustomerId", label: "Manager Account ID (MCC)", placeholder: "987-654-3210", hint: "Required if using a manager / MCC account" },
    ],
  },
  {
    key: "LinkedIn",
    label: "LinkedIn Lead Gen",
    brand: "LinkedIn",
    icon: "fab fa-linkedin",
    color: "#0A66C2",
    description:
      "Capture leads from LinkedIn Lead Gen Forms via the LinkedIn Marketing API. " +
      "Each form submission triggers a webhook that pushes the lead into the CRM.",
    fields: [
      { name: "clientId", label: "App Client ID", placeholder: "78xxxxxxxx", hint: "From LinkedIn Developer portal → your app" },
      { name: "clientSecret", label: "App Client Secret", placeholder: "xxxxxxxxxxxxxxxx", secret: true },
      { name: "accessToken", label: "Access Token", placeholder: "AQX…", secret: true, hint: "OAuth 2.0 token with r_liteprofile + rw_leads scopes" },
      { name: "accountId", label: "Ad Account ID (URN)", placeholder: "urn:li:sponsoredAccount:123456789", hint: "From Campaign Manager URL" },
      { name: "campaignGroupId", label: "Campaign Group ID", placeholder: "123456789", hint: "Optional — filter leads to one campaign group" },
    ],
  },
  {
    key: "Instagram",
    label: "Instagram Leads",
    brand: "Meta / Instagram",
    icon: "fab fa-instagram",
    color: "#C13584",
    description:
      "Pull leads from Instagram Lead Ads using the Meta Graph API. " +
      "Requires a Facebook App connected to the Instagram Business account. " +
      "Different from Facebook Lead Ads — each Instagram account stores its own credentials below.",
    fields: [
      { name: "appId", label: "Meta App ID", placeholder: "1234567890123456", hint: "From developers.facebook.com → your App" },
      { name: "appSecret", label: "Meta App Secret", placeholder: "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx", secret: true },
      { name: "accessToken", label: "Page Access Token", placeholder: "EAAx…", secret: true, hint: "Long-lived Page token with leads_retrieval permission" },
      { name: "businessAccountId", label: "Instagram Business Account ID", placeholder: "17841400…", hint: "Found under Instagram account Settings → Professional Account" },
      { name: "formId", label: "Lead Ad Form ID (optional)", placeholder: "123456789", hint: "Leave blank to pull from all forms on this account" },
    ],
  },
  {
    key: "NinetyNineAcres",
    label: "99acres",
    brand: "99acres",
    icon: "fas fa-city",
    color: "#E84545",
    description:
      "Receive leads from 99acres via XML push or pull API. " +
      "Configure the account API key and the listing ID(s) to map incoming enquiries to the right project in the CRM.",
    fields: [
      { name: "apiKey", label: "API Key", placeholder: "xxxxxxxxxxxxxxxx", secret: true, hint: "From 99acres partner portal → Integrations" },
      { name: "listingId", label: "Primary Listing ID", placeholder: "P12345678", hint: "Main residential / commercial project listing" },
      { name: "xmlEndpoint", label: "XML Push Endpoint (if custom)", placeholder: "https://…", hint: "Leave blank to use the default 99acres endpoint" },
      { name: "sourceEmail", label: "Lead Notification Email", placeholder: "leads@agency.com", hint: "Email 99acres sends enquiry notifications to" },
    ],
  },
];

/* ────────────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────────────── */
function parseConfig(configJson) {
  try {
    return configJson ? JSON.parse(configJson) : {};
  } catch {
    return {};
  }
}

function emptyForm(channel) {
  return channel.fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {
    isEnabled: false,
  });
}

/* ────────────────────────────────────────────────────────────────
   Single-channel card
   ──────────────────────────────────────────────────────────────── */
function ChannelCard({ channel, clientId, toast }) {
  const [form, setForm] = useState(emptyForm(channel));
  const [recordId, setRecordId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const row = await ChannelIntegrationService.getByClientIdAndChannel(clientId, channel.key);
      if (row && row.id) {
        const cfg = parseConfig(row.configJson);
        setRecordId(row.id);
        const populated = { isEnabled: !!row.isEnabled };
        channel.fields.forEach((f) => {
          populated[f.name] = f.secret ? "" : (cfg[f.name] ?? "");
        });
        setForm(populated);
      } else {
        setRecordId(0);
        setForm(emptyForm(channel));
      }
    } catch {
      setForm(emptyForm(channel));
    } finally {
      setLoading(false);
    }
  }, [clientId, channel]);

  useEffect(() => {
    load();
  }, [load]);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cfg = {};
      channel.fields.forEach((f) => {
        if (!f.secret || form[f.name]) cfg[f.name] = form[f.name];
      });

      await ChannelIntegrationService.upsert({
        id: recordId,
        clientId: Number(clientId),
        channelType: channel.key,
        isEnabled: form.isEnabled,
        configJson: JSON.stringify(cfg),
        isActive: true,
        createdBy: 0,
        createdOn: new Date().toISOString(),
        modifiedBy: 0,
        modifiedOn: new Date().toISOString(),
      });

      toast.current?.show({ severity: "success", summary: "Saved", detail: `${channel.label} settings saved.`, life: 2800 });
      await load();
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: `Could not save ${channel.label} settings.`, life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0 18px" };

  if (loading) {
    return (
      <SaCard>
        <p style={{ margin: 0, color: SA.muted, fontSize: "13px" }}>Loading {channel.label}…</p>
      </SaCard>
    );
  }

  return (
    <form onSubmit={save}>
      <SaCard>
        {/* Header row */}
        <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "14px" }}>
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              background: `${channel.color}18`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              color: channel.color,
              flexShrink: 0,
            }}
          >
            <i className={channel.icon} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: "0 0 2px", fontSize: "10px", fontWeight: "800", color: channel.color, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              {channel.brand}
            </p>
            <SaSectionTitle iconClass={null}>{channel.label}</SaSectionTitle>
          </div>
          {/* Enable toggle */}
          <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
            <div
              style={{
                width: "42px",
                height: "22px",
                borderRadius: "11px",
                background: form.isEnabled ? SA.success : SA.border,
                position: "relative",
                transition: "background .2s",
                cursor: "pointer",
              }}
              onClick={() => setForm((p) => ({ ...p, isEnabled: !p.isEnabled }))}
            >
              <div
                style={{
                  position: "absolute",
                  top: "3px",
                  left: form.isEnabled ? "22px" : "3px",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left .2s",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                }}
              />
            </div>
            <span style={{ fontSize: "12px", fontWeight: "700", color: form.isEnabled ? SA.success : SA.muted }}>
              {form.isEnabled ? "Enabled" : "Disabled"}
            </span>
          </label>
        </div>

        <p style={{ margin: "-4px 0 18px", fontSize: "13px", color: SA.muted, lineHeight: 1.6, borderBottom: `1px solid ${SA.border}`, paddingBottom: "14px" }}>
          {channel.description}
        </p>

        <div style={grid}>
          {channel.fields.map((f) => (
            <SaField key={f.name} label={f.label} hint={f.hint}>
              <input
                name={f.name}
                type={f.secret ? "password" : "text"}
                autoComplete={f.secret ? "new-password" : "off"}
                value={form[f.name]}
                onChange={change}
                placeholder={f.placeholder ?? ""}
                style={saInputSx}
              />
              {f.secret && recordId > 0 && (
                <p style={{ margin: "4px 0 0", fontSize: "11px", color: SA.muted }}>
                  Leave blank to keep the existing secret.
                </p>
              )}
            </SaField>
          ))}
        </div>

        <SaFormActions>
          <SaPrimaryButton type="submit" disabled={saving} iconClass={saving ? "fas fa-spinner fa-spin" : "fas fa-save"}>
            {saving ? "Saving…" : recordId ? `Update ${channel.label}` : `Save ${channel.label}`}
          </SaPrimaryButton>
        </SaFormActions>
      </SaCard>
    </form>
  );
}

/* ────────────────────────────────────────────────────────────────
   Main export — renders one ChannelCard per CHANNELS entry
   ──────────────────────────────────────────────────────────────── */
function ChannelIntegration({ ClientId, channelKey }) {
  const toast = useRef(null);
  const visibleChannels = channelKey ? CHANNELS.filter((ch) => ch.key === channelKey) : CHANNELS;

  if (channelKey && visibleChannels.length === 0) {
    return null;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Toast ref={toast} />
      {visibleChannels.map((ch) => (
        <ChannelCard key={ch.key} channel={ch} clientId={ClientId} toast={toast} />
      ))}
    </div>
  );
}

export { CHANNELS };
export default ChannelIntegration;
